import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Plus, ExternalLink, RefreshCw, Search, User, CalendarClock } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import TaskDetail from "./TaskDetail";
import { QuickAddTask } from "./QuickAddTask";

const NOTION_COLORS = {
  gray: "#9B9A97", brown: "#937264", orange: "#D9730D", yellow: "#CB912F",
  green: "#448361", blue: "#337EA9", purple: "#9065B0", pink: "#C14C8A",
  red: "#D44C47", default: "#787774",
};
const colorOf = (c) => NOTION_COLORS[c] || NOTION_COLORS.default;
const NO_STATUS = "__none__";

function startOfToday() { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }
function dueMatch(task, mode) {
  if (mode === "all") return true;
  if (mode === "none") return !task.due;
  if (!task.due) return false;
  const d = new Date(task.due); const today = startOfToday();
  if (mode === "overdue") return d < today;
  if (mode === "week") { const wk = new Date(today); wk.setDate(wk.getDate() + 7); return d >= today && d <= wk; }
  return true;
}

function Badge({ b }) {
  return <span className="inline-block text-[10px] px-1.5 py-0.5 rounded bg-brand-ink/5 text-brand-muted truncate max-w-[130px]">{b.label}</span>;
}

function Card({ task, onDragStart, onEdit }) {
  return (
    <div draggable onDragStart={() => onDragStart(task)} onClick={() => onEdit(task)}
      data-testid={`board-card-${task.id}`}
      className="group bg-brand-surface border border-brand-border rounded-lg p-3 cursor-grab active:cursor-grabbing hover:border-brand-primary hover:shadow-sm transition-all duration-150">
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm text-brand-ink leading-snug">{task.title}</p>
        <a href={task.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
          className="shrink-0 text-brand-tertiary hover:text-brand-ink opacity-0 group-hover:opacity-100 transition-opacity" title="Open in Notion">
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>
      {task.badges?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">{task.badges.map((b, i) => <Badge key={i} b={b} />)}</div>
      )}
    </div>
  );
}

function Column({ col, tasks, onDrop, onDragStart, onEdit, onAdd, addCtx }) {
  const [over, setOver] = useState(false);
  const [adding, setAdding] = useState(false);
  return (
    <div className="w-72 shrink-0 flex flex-col" data-testid={`board-col-${col.name}`}>
      <div className="flex items-center justify-between px-1 mb-2">
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full" style={{ background: colorOf(col.color) }} />
          <span className="text-sm font-medium text-brand-ink">{col.label}</span>
          <span className="text-xs text-brand-tertiary">{tasks.length}</span>
        </div>
        <button onClick={() => setAdding((v) => !v)} data-testid={`board-add-${col.name}`}
          className="text-brand-tertiary hover:text-brand-ink"><Plus className="h-4 w-4" /></button>
      </div>
      <div onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
        onDrop={() => { setOver(false); onDrop(col.name); }}
        className={`flex-1 rounded-lg p-2 space-y-2 min-h-[120px] transition-colors ${over ? "bg-brand-primary/5 ring-1 ring-brand-primary/40" : "bg-brand-hover/40"}`}>
        {adding && <QuickAddTask colName={col.name} ctx={addCtx}
          onSubmit={(c, t, extra) => { onAdd(c, t, extra); setAdding(false); }} onCancel={() => setAdding(false)} />}
        {tasks.map((t) => <Card key={t.id} task={t} onDragStart={onDragStart} onEdit={onEdit} />)}
      </div>
    </div>
  );
}


export default function NotionBoard({ boardId, location = null, showToolbar = true }) {
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drag, setDrag] = useState(null);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [assignee, setAssignee] = useState("all");
  const [due, setDue] = useState("all");

  const loadSeq = useRef(0);
  const load = useCallback(async () => {
    if (!boardId) return;
    const req = ++loadSeq.current;
    setLoading(true);
    try {
      const { data } = await api.get(`/notion/boards/${boardId}`, { params: location ? { location } : {} });
      if (req === loadSeq.current) setBoard(data);
    } catch (e) { if (req === loadSeq.current) { toast.error("Could not load board", { description: e.userMessage }); setBoard(null); } }
    finally { if (req === loadSeq.current) setLoading(false); }
  }, [boardId, location]);

  useEffect(() => { load(); }, [load]);

  const [addCtx, setAddCtx] = useState(null);
  useEffect(() => {
    if (!boardId) return;
    let live = true;
    setAddCtx(null);
    Promise.all([api.get(`/notion/boards/${boardId}/meta`), api.get("/notion/users"), api.get("/notion/offices")])
      .then(([m, u, o]) => live && setAddCtx({ meta: m.data, users: u.data.users || [], offices: o.data.offices || [], location }))
      .catch(() => live && setAddCtx(null));
    return () => { live = false; };
  }, [boardId, location]);

  const columns = board ? [
    ...board.options.map((o) => ({ name: o.name, label: o.name, color: o.color })),
    { name: NO_STATUS, label: "No " + (board.group_prop || "status"), color: "gray" },
  ] : [];

  const assignees = board ? Array.from(new Set((board.tasks || []).flatMap((t) => t.assignees || []))).sort() : [];

  const filtered = (board?.tasks || []).filter((t) =>
    (!search || t.title.toLowerCase().includes(search.toLowerCase())) &&
    (assignee === "all" || (t.assignees || []).includes(assignee)) &&
    dueMatch(t, due));
  const tasksByCol = (name) => filtered.filter((t) => (t.group || NO_STATUS) === name);

  const onDrop = async (colName) => {
    if (!drag) return;
    const task = drag; setDrag(null);
    const target = colName === NO_STATUS ? "" : colName;
    if ((task.group || "") === target) return;
    setBoard((b) => ({ ...b, tasks: b.tasks.map((t) => t.id === task.id ? { ...t, group: target || null } : t) }));
    try {
      await api.patch(`/notion/tasks/${task.id}`, { group: target, group_prop: board.group_prop, group_type: board.group_type });
    } catch { toast.error("Move failed"); load(); }
  };

  const onAdd = async (colName, title, extra = {}) => {
    const group = colName === NO_STATUS ? null : colName;
    try { await api.post(`/notion/boards/${boardId}/tasks`, { title, group, ...extra, location: location || undefined }); toast.success("Task created"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div>
      {showToolbar && (
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-tertiary" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search tasks…" data-testid="board-search"
              className="pl-9 pr-3 py-2 text-sm border border-brand-border rounded-lg bg-brand-surface text-brand-ink" />
          </div>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-tertiary" />
            <select value={assignee} onChange={(e) => setAssignee(e.target.value)} data-testid="board-filter-assignee"
              className="pl-9 pr-8 py-2 text-sm border border-brand-border rounded-lg bg-brand-surface text-brand-ink max-w-[200px]">
              <option value="all">All assignees</option>
              {assignees.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div className="relative">
            <CalendarClock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-tertiary" />
            <select value={due} onChange={(e) => setDue(e.target.value)} data-testid="board-filter-due"
              className="pl-9 pr-8 py-2 text-sm border border-brand-border rounded-lg bg-brand-surface text-brand-ink">
              <option value="all">Any due date</option>
              <option value="overdue">Overdue</option>
              <option value="week">Due this week</option>
              <option value="none">No due date</option>
            </select>
          </div>
          <button onClick={load} data-testid="board-refresh"
            className="p-2 border border-brand-border rounded-lg text-brand-muted hover:text-brand-ink" title="Refresh from Notion">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </button>
          {board?.url && (
            <a href={board.url} target="_blank" rel="noreferrer" className="ml-auto text-sm text-brand-muted hover:text-brand-ink inline-flex items-center gap-1.5">
              <ExternalLink className="h-4 w-4" /> Open in Notion
            </a>
          )}
        </div>
      )}

      {loading && !board ? (
        <div className="flex gap-4">{[...Array(4)].map((_, i) => <div key={i} className="w-72 h-64 rounded-lg bg-brand-surface border border-brand-border animate-pulse" />)}</div>
      ) : board ? (
        <>
          {board.tasks.length === 0 && location && (
            <div className="text-sm text-brand-muted pb-4" data-testid="board-no-location-tasks">No tasks for this location on “{board.title}” yet — add one below.</div>
          )}
          <div className="flex gap-4 overflow-x-auto pb-4" data-testid="board-kanban">
            {columns.map((col) => (
              <Column key={col.name} col={col} tasks={tasksByCol(col.name)}
                onDrop={onDrop} onDragStart={setDrag} onEdit={setEditing} onAdd={onAdd} addCtx={addCtx} />
            ))}
          </div>
        </>
      ) : null}

      {editing && board && <TaskDetail pageId={editing.id} onClose={() => setEditing(null)}
        onChanged={load} />}
    </div>
  );
}
